import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export type InlineInputType = 'text' | 'email' | 'tel';

@customElement('befit-inline-input')
export class InlineInput extends LitElement {
  static override styles = css`
    :host {
      display: block;
      margin: 8px 0;
      animation: bf-inline-in 0.22s ease-out both;
    }
    @keyframes bf-inline-in {
      from { opacity: 0; transform: translateY(-6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @media (prefers-reduced-motion: reduce) {
      :host { animation: none; }
    }
    .wrap {
      background: white;
      border: 1px solid var(--befit-border, #E5E7EB);
      border-left: 3px solid var(--befit-accent, #8FFE09);
      border-radius: 12px;
      padding: 14px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    .label {
      font-size: 12px;
      color: var(--befit-text, #475467);
      margin: 0;
      line-height: 1.4;
      font-weight: 600;
    }
    form {
      display: flex;
      gap: 8px;
    }
    input {
      flex: 1;
      font: inherit;
      font-size: 14px;
      padding: 9px 11px;
      border: 1px solid var(--befit-border, #E5E7EB);
      border-radius: 8px;
      background: white;
      color: var(--befit-heading, #1D2939);
      outline: none;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }
    input:focus {
      border-color: var(--befit-primary, #101828);
      box-shadow: 0 0 0 3px var(--befit-accent-soft, rgba(143, 254, 9, 0.14));
    }
    input::placeholder { color: var(--befit-muted, #98A2B3); }
    /* Prevent iOS Safari zoom-on-focus: inputs under 16px trigger it. Bump to
       16px on phones (<=600px); desktop keeps the 14px design. */
    @media (max-width: 600px) {
      input { font-size: 16px; }
    }
    button {
      padding: 9px 14px;
      background: var(--befit-primary, #101828);
      color: white;
      border: 2px solid var(--befit-primary, #101828);
      border-radius: 8px;
      font: inherit;
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    button:not(:disabled):hover {
      background: var(--befit-accent, #8FFE09);
      color: var(--befit-primary, #101828);
      border-color: var(--befit-accent, #8FFE09);
    }
    button:disabled {
      background: var(--befit-muted, #98A2B3);
      border-color: var(--befit-muted, #98A2B3);
      cursor: not-allowed;
    }
    .submitted {
      background: var(--befit-bg-soft, #F9FAFB);
      border-left: 3px solid var(--befit-accent, #8FFE09);
      padding: 12px 14px;
      border-radius: 10px;
      font-size: 13px;
      color: var(--befit-text, #475467);
    }
    .submitted strong { color: var(--befit-heading, #1D2939); }
    .hint {
      margin: 0;
      padding-top: 4px;
      border-top: 1px dashed var(--befit-border, #E5E7EB);
      font-size: 11px;
      line-height: 1.4;
      color: var(--befit-text, #475467);
    }
    .hint strong { color: var(--befit-heading, #1D2939); font-weight: 600; }
  `;

  @property({ type: String }) field = '';
  @property({ type: String }) label = '';
  @property({ type: String }) type: InlineInputType = 'text';
  @property({ type: String }) placeholder = '';
  @property({ type: String }) prefill = '';
  @state() private value = '';
  @state() private submitted = false;

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has('prefill') && this.prefill && !this.value) {
      this.value = this.prefill;
    }
  }

  override updated(changed: Map<string, unknown>) {
    if (changed.has('field')) {
      const input = this.renderRoot.querySelector('input') as HTMLInputElement | null;
      input?.focus();
    }
  }

  private valid(): boolean {
    const t = this.value.trim();
    if (!t) return false;
    if (this.type === 'email') return /^\S+@\S+\.\S+$/.test(t);
    if (this.type === 'tel') return /^[\d\s+()-]{6,}$/.test(t);
    return t.length > 0;
  }

  private submit(e: Event) {
    e.preventDefault();
    if (!this.valid()) return;
    this.submitted = true;
    this.dispatchEvent(
      new CustomEvent('inline-submit', {
        detail: { field: this.field, label: this.label, value: this.value.trim() },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    if (this.submitted) {
      return html`<div class="submitted"><strong>Mulțumesc!</strong> Continuăm.</div>`;
    }
    return html`
      <div class="wrap">
        ${this.label ? html`<p class="label">${this.label}</p>` : ''}
        <form @submit=${this.submit}>
          <input
            type=${this.type}
            placeholder=${this.placeholder}
            .value=${this.value}
            @input=${(e: Event) => (this.value = (e.target as HTMLInputElement).value)}
            required
            autofocus
          />
          <button type="submit" ?disabled=${!this.valid()}>Trimite</button>
        </form>
        <p class="hint">
          <strong>Completează aici</strong> sau răspunde direct în câmpul de chat, jos.
        </p>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'befit-inline-input': InlineInput;
  }
}
